import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { DiscardGuard } from '@/hooks/use-unsaved'

/**
 * The question a dialog asks before throwing away what someone typed.
 *
 * Rendered INSIDE the dialog it guards, as a `fixed` layer above it rather than
 * a second Radix dialog. Three reasons:
 *
 * - A nested Radix dialog takes the Escape key and the focus trap off the
 *   editor underneath, and gives the user two overlays to dismiss.
 * - `fixed` rather than `absolute` because `DialogContent` scrolls its own
 *   overflow: an absolute child is placed against the full scroll height, so a
 *   long form would ask the question somewhere above the fold.
 * - Staying a descendant of the content keeps Radix's focus trap honest — it
 *   holds focus inside the dialog, and this is inside the dialog.
 *
 * "Keep editing" is the focused, default action. The destructive one should
 * take a deliberate move, and Enter is what a hand already on the keyboard
 * presses.
 */
export function DiscardPrompt({
  guard,
  what = 'your changes',
}: {
  guard: DiscardGuard
  /** Names what is at stake: "this 1:1 note", "this topic". */
  what?: string
}) {
  if (!guard.asking) return null
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onKeyDown={(e) => {
        // Escape here answers THIS question, and must not reach the dialog
        // underneath — which would read it as another request to close.
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          guard.keepEditing()
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="discard-prompt-title"
        aria-describedby="discard-prompt-body"
        className="w-full max-w-sm rounded-lg border bg-background p-5 shadow-lg"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="min-w-0">
            <p id="discard-prompt-title" className="font-semibold">
              Discard {what}?
            </p>
            <p id="discard-prompt-body" className="mt-1 text-sm text-muted-foreground">
              Nothing has been saved yet. Closing now throws away what you typed, and there is no
              way to get it back.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="destructive" onClick={guard.discard}>
            Discard
          </Button>
          <Button type="button" autoFocus onClick={guard.keepEditing}>
            Keep editing
          </Button>
        </div>
      </div>
    </div>
  )
}
